import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Check, Loader2, Save, ShieldAlert, Undo2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { previewGrants, replaceGrants } from './api'
import type {
  GrantChangePreviewResponse, PermissionModuleResponse, RoleResponse,
} from '@/shared/api/types'

/**
 * The grant editor: permissions grouped by module, toggled, previewed, then saved.
 *
 * `PUT /roles/{id}/permissions` is a FULL REPLACE, so the local set is the whole
 * desired state and unticking is as meaningful as ticking.
 *
 * Nothing saves without a preview first. Changing a role's grants silently
 * re-permissions every holder of that role, and the preview is the only place
 * that number is visible before it becomes true.
 */
export function PermissionMatrix({
  role, modules, onSaved,
}: {
  role: RoleResponse
  modules: PermissionModuleResponse[]
  onSaved: () => void
}) {
  const canUpdate = useCan('rbac:update')
  const original = useMemo(
    () => new Set(role.permissions.map((p) => p.id)),
    [role],
  )
  const [selected, setSelected] = useState<Set<string>>(original)
  const [preview, setPreview] = useState<GrantChangePreviewResponse | null>(null)
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setSelected(new Set(role.permissions.map((p) => p.id)))
    setPreview(null)
    setError(null)
    setSaved(false)
  }, [role])

  const dirty = useMemo(() => {
    if (selected.size !== original.size) return true
    for (const id of selected) if (!original.has(id)) return true
    return false
  }, [selected, original])

  const toggle = (id: string) => {
    setPreview(null)
    setSaved(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleModule = (mod: PermissionModuleResponse) => {
    const ids = mod.permissions.map((p) => p.id)
    const allOn = ids.every((id) => selected.has(id))
    setPreview(null)
    setSaved(false)
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of ids) { if (allOn) next.delete(id); else next.add(id) }
      return next
    })
  }

  const reset = () => {
    setSelected(new Set(original))
    setPreview(null)
    setSaved(false)
  }

  async function runPreview() {
    setBusy('preview'); setError(null)
    try {
      setPreview(await previewGrants(role.id, [...selected]))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function save() {
    setBusy('save'); setError(null)
    try {
      await replaceGrants(role.id, [...selected])
      setPreview(null)
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const readOnly = !canUpdate

  return (
    <Panel
      title="Permissions"
      description={
        readOnly
          ? 'Read-only — you do not hold rbac:update.'
          : `${selected.size} of ${modules.reduce((n, m) => n + m.permissions.length, 0)} granted. Changes apply to everyone holding this role.`
      }
      bodyClassName="p-0"
      right={
        !readOnly ? (
          <>
            {dirty && (
              <button className="erp-btn" onClick={reset} disabled={busy !== null}>
                <Undo2 className="h-3.5 w-3.5" /> Reset
              </button>
            )}
            <button
              className="erp-btn"
              onClick={runPreview}
              disabled={!dirty || busy !== null}
              title={dirty ? 'See what changes and who is affected' : 'No changes to preview'}
            >
              {busy === 'preview' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Preview
            </button>
            <button
              className="erp-btn erp-btn-primary"
              onClick={save}
              // Preview is mandatory: no saving a blast radius you have not seen.
              disabled={!dirty || !preview || preview.refusedByEscalationGuard || busy !== null}
              title={!preview ? 'Run a preview first' : undefined}
            >
              {busy === 'save' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save grants
            </button>
          </>
        ) : undefined
      }
    >
      <ErrorStrip message={error} />

      {saved && (
        <div className="p-4 pb-0">
          <Note tone="good" onDismiss={() => setSaved(false)}>
            Grants saved. Authorities are resolved per request, so holders see the change immediately.
          </Note>
        </div>
      )}

      {preview && (
        <div className="p-4 pb-0 space-y-2">
          {preview.refusedByEscalationGuard ? (
            <Note tone="bad">
              <span className="font-medium">Refused.</span>{' '}
              {preview.refusalReason
                ?? 'You cannot grant a permission you do not hold yourself.'}
            </Note>
          ) : (
            <div className="erp-card p-3 space-y-2">
              <div className="text-erp-sm">
                <span className="font-medium">
                  {preview.added.length} added · {preview.removed.length} removed
                </span>
                {' — '}
                affects <span className="font-medium">{fmtNumber(preview.affectedUserCount)}</span>{' '}
                user{preview.affectedUserCount === 1 ? '' : 's'} holding this role.
              </div>
              {preview.dangerousAdded.length > 0 && (
                <div className="flex items-start gap-2 text-erp-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Includes dangerous permissions:{' '}
                    <span className="font-mono text-erp-xs">
                      {preview.dangerousAdded.join(', ')}
                    </span>
                  </span>
                </div>
              )}
              {preview.added.length > 0 && (
                <ChipList label="Added" tone="good" items={preview.added.map((p) => p.authority)} />
              )}
              {preview.removed.length > 0 && (
                <ChipList label="Removed" tone="bad" items={preview.removed.map((p) => p.authority)} />
              )}
            </div>
          )}
        </div>
      )}

      <div className="p-4 space-y-4">
        {modules.length === 0 && (
          <p className="text-erp-sm text-muted-foreground">Permission catalogue is empty.</p>
        )}
        {modules.map((mod) => {
          const ids = mod.permissions.map((p) => p.id)
          const on = ids.filter((id) => selected.has(id)).length
          const allOn = ids.length > 0 && on === ids.length
          return (
            <section key={mod.module} className="border border-border rounded-md overflow-hidden">
              <header className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-erp-sm font-semibold truncate">{mod.displayName}</div>
                  {mod.description && (
                    <div className="text-erp-xs text-muted-foreground truncate">{mod.description}</div>
                  )}
                </div>
                <span className="text-erp-xs text-muted-foreground erp-metric shrink-0">
                  {on}/{ids.length}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => toggleModule(mod)}
                    className="erp-btn shrink-0"
                  >
                    {allOn ? 'None' : 'All'}
                  </button>
                )}
              </header>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3">
                {mod.permissions.map((p) => {
                  const checked = selected.has(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={readOnly}
                      onClick={() => toggle(p.id)}
                      className={cn(
                        'text-left px-3 py-2 bg-background flex items-start gap-2.5 min-w-0',
                        'border-b border-r border-border',
                        !readOnly && 'hover:bg-muted/50',
                        readOnly && 'cursor-default',
                      )}
                    >
                      <span className={cn(
                        'mt-0.5 h-3.5 w-3.5 shrink-0 border rounded-[3px] inline-flex items-center justify-center',
                        checked
                          ? 'bg-chocolate border-chocolate text-chocolate-foreground'
                          : 'border-input',
                      )}>
                        {checked && <Check className="h-2.5 w-2.5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-erp-sm block truncate flex items-center gap-1.5">
                          {p.displayName}
                          {p.dangerous && (
                            <ShieldAlert className="h-3 w-3 text-amber-600 shrink-0"
                                         aria-label="Dangerous permission" />
                          )}
                        </span>
                        <span className="text-erp-xs font-mono text-muted-foreground block truncate">
                          {p.authority}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>
          )
        })}

        <p className="text-erp-xs text-muted-foreground">
          Permissions themselves are defined in code and shipped by migration — they cannot
          be created here. Each one maps to a check the API actually performs, so a
          permission the backend does not know about would grant nothing. Roles are the
          part you compose.
        </p>
      </div>
    </Panel>
  )
}

function ChipList({
  label, items, tone,
}: { label: string; items: string[]; tone: 'good' | 'bad' }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-erp-xs text-muted-foreground">{label}:</span>
      {items.map((a) => (
        <span
          key={a}
          className={cn(
            'px-1.5 py-0.5 rounded-md border font-mono text-erp-xs',
            tone === 'good'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200',
          )}
        >
          {a}
        </span>
      ))}
    </div>
  )
}
